import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  assignStoreOwner,
  fetchActivationOverview,
  deactivateLocationUser,
  fetchDirectoryAudit,
  fetchDirectoryDrift,
  fetchLocationAdmins,
  fetchLocationUsers,
  fetchUnassignedStores,
  refreshWorkvivoDirectory,
  resendWelcomeEmail
} from "./api";
import { extractAxiosDetail } from "./format";
import type {
  ActivationOverview,
  DirectoryAuditPage,
  DirectoryDrift,
  LocationAdminListPage,
  LocationAdminsParams,
  LocationUser,
  LocationUserListPage,
  LocationUsersParams,
  UnassignedStoreList,
  WorkvivoDirectoryRefreshProgress
} from "./types";

export const useLocationAdmins = (params: LocationAdminsParams, options?: { enabled?: boolean }) =>
  useQuery<LocationAdminListPage>({
    queryKey: ["directory", "location-admins", params],
    queryFn: () => fetchLocationAdmins(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true
  });

export const useLocationUsers = (params: LocationUsersParams, options?: { enabled?: boolean }) =>
  useQuery<LocationUserListPage>({
    queryKey: ["directory", "location-users", params],
    queryFn: () => fetchLocationUsers(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true
  });

/**
 * Search box state with a debounced committed value. `onCommit` fires in the same
 * (batched) update as the committed query changes, so callers can reset paging
 * without an extra fetch for the stale page.
 */
export function useDebouncedSearch(delayMs = 300, onCommit?: () => void) {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    const next = input.trim();
    if (next === query) return;
    const timer = window.setTimeout(() => {
      setQuery(next);
      onCommitRef.current?.();
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [input, query, delayMs]);

  return { input, setInput, query };
}

const MAX_REFRESH_ITERATIONS = 60;

/**
 * Drives the on-demand Workvivo directory cache refresh: each POST call does ~20s of
 * work and returns partial progress, so `start()` loops (awaiting each call) until the
 * server reports `done` or `status === "error"`, then invalidates the directory queries
 * so pages pick up the freshly cached data.
 */
export function useWorkvivoDirectoryRefresh() {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<WorkvivoDirectoryRefreshProgress | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runningRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    []
  );

  const start = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setIsRunning(true);
    setError(null);

    let last: WorkvivoDirectoryRefreshProgress | null = null;
    try {
      let iterations = 0;
      do {
        last = await refreshWorkvivoDirectory();
        if (cancelledRef.current) return;
        setProgress(last);
        iterations += 1;
      } while (!last.done && last.status !== "error" && iterations < MAX_REFRESH_ITERATIONS);

      if (last.status === "error") {
        setError(last.last_error || "Workvivo directory refresh failed.");
      }
    } catch (e) {
      if (!cancelledRef.current) {
        setError(extractAxiosDetail(e));
      }
    } finally {
      runningRef.current = false;
      if (!cancelledRef.current) {
        setIsRunning(false);
        void queryClient.invalidateQueries({ queryKey: ["directory"] });
      }
    }
  }, [queryClient]);

  return { start, isRunning, progress, error };
}

export const UNASSIGNED_STORES_QUERY_KEY = ["directory", "unassigned-stores"] as const;

export const useUnassignedStores = (options?: { enabled?: boolean }) =>
  useQuery<UnassignedStoreList>({
    queryKey: UNASSIGNED_STORES_QUERY_KEY,
    queryFn: fetchUnassignedStores,
    enabled: options?.enabled ?? true
  });

/** Admin typeahead for store assignment (`/users/owners` doesn't return ids, so search location admins). */
export const useLocationAdminSearch = (q: string, options?: { enabled?: boolean }) =>
  useQuery<LocationAdminListPage>({
    queryKey: ["directory", "location-admins", "typeahead", q],
    queryFn: () => fetchLocationAdmins({ q, includeInactive: false, limit: 8, offset: 0 }),
    enabled: (options?.enabled ?? true) && q.trim().length > 0,
    staleTime: 30_000
  });

export const useDirectoryDrift = (options?: { enabled?: boolean }) =>
  useQuery<DirectoryDrift>({
    queryKey: ["directory", "drift"],
    queryFn: fetchDirectoryDrift,
    enabled: options?.enabled ?? true
  });

export const useActivationOverview = (options?: { enabled?: boolean }) =>
  useQuery<ActivationOverview>({
    queryKey: ["directory", "activation-overview"],
    queryFn: fetchActivationOverview,
    enabled: options?.enabled ?? true
  });

export const useDirectoryAudit = (params: { limit: number; offset: number }, options?: { enabled?: boolean }) =>
  useQuery<DirectoryAuditPage>({
    queryKey: ["directory", "audit", params],
    queryFn: () => fetchDirectoryAudit(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true
  });

export const useResendWelcomeEmail = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ownerId: string) => resendWelcomeEmail(ownerId),
    onSettled: () => {
      // A 502 still resets the temporary password server-side, so refresh either way.
      void client.invalidateQueries({ queryKey: ["directory"] });
    }
  });
};

export const useAssignStoreOwner = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (args: { storeId: string; ownerId: string }) => assignStoreOwner(args.storeId, args.ownerId),
    onSuccess: (_data, args) => {
      client.setQueryData<UnassignedStoreList>(UNASSIGNED_STORES_QUERY_KEY, (prev) =>
        prev
          ? { items: prev.items.filter((s) => s.store_id !== args.storeId), total: Math.max(0, prev.total - 1) }
          : prev
      );
      void client.invalidateQueries({ queryKey: ["directory"] });
    }
  });
};

/**
 * Deactivates a Workvivo user. On success the row is patched to `active: false` in every
 * cached location-users page (so it updates instantly), then directory queries are invalidated.
 */
export const useDeactivateLocationUser = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (args: { workvivoId: string; confirmEmail: string }) =>
      deactivateLocationUser(args.workvivoId, args.confirmEmail),
    onSuccess: (updated, args) => {
      client.setQueriesData<LocationUserListPage>({ queryKey: ["directory", "location-users"] }, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map(
                (u): LocationUser => (u.workvivo_id === args.workvivoId ? { ...u, ...updated, active: false } : u)
              )
            }
          : prev
      );
      void client.invalidateQueries({ queryKey: ["directory"] });
    }
  });
};
